import { Injectable } from '@angular/core';
import { ProducerService } from './producer.service';
import { RecorderService } from './recorder.service';
import { Observable, of } from 'rxjs';
import { IProducer } from '../../../interfaces/producer.interface';
import { catchError, combineLatestWith, map } from 'rxjs/operators';
import { IRecorder } from '../interfaces/recorder.interface';
import { IStream } from '../../../interfaces/stream.interface';
import { StreamTypeEnum } from '../../../enums/stream-type.enum';
import { ILive } from '../interfaces/live.interface';
import { LiveStatus } from '../enums/live-status.enum';
import { ProducerType } from '../../../enums/producer-type.enum';
import { LiveType } from '../enums/live-type.enum';
import { RecorderStreamProtocol } from '../interfaces/recorder-stream.interface';
import { PermissionService } from '../../../services/permission.service';
import { Permission } from '../../../enums/permission.enum';
import { ICameraRegel, IVeldGroep } from '../interfaces/veld-groep.interface';
import { IRecorderStream, RecorderStatus } from '../interfaces/recorder-stream.interface';
import { studioOffline } from '../helpers/studio-online';

/** Groepsnaam voor camera's die nog aan geen enkel veld hangen. */
const ZONDER_VELD = '';

@Injectable({
  providedIn: 'root'
})
export class LiveService {

  constructor(private producerService: ProducerService, private recorderService: RecorderService,
              private permissionService: PermissionService) {
  }

  public list(): Observable<ILive[]> {
    return this.getLiveFromProducers$().pipe(
      combineLatestWith(this.getLiveFromRecorders$()),
      map(([producers, recorders]) => [...producers, ...recorders])
    );
  }

  /**
   * De Live-pagina toont velden met hun camera's, en niet de computers waar
   * die camera's aan hangen: "Provispo-PC-Office" zegt een analist of trainer
   * niets (eigenaar 15-09-2026).
   *
   * Een camera kan op meer dan één veld uitkijken en staat dan in beide
   * groepen -- dat is dezelfde camera, geen kopie. Camera's zonder veld
   * verdwijnen niet maar komen onderaan in een eigen groep, anders is een nog
   * niet ingedeelde camera stilletjes onvindbaar.
   */
  public groepeer(items: ILive[]): IVeldGroep[] {
    // Per locatie én veld: "Veld 1" op twee locaties zijn twee velden
    // (eigenaar 30-10-2026, studio's op meer dan één plek).
    const perVeld = new Map<string, { naam: string; locatie: string; cameras: ICameraRegel[] }>();

    const zet = (locatie: string, veld: string, camera: ICameraRegel) => {
      const sleutel = `${locatie}\u0000${veld}`;
      const groep = perVeld.get(sleutel);
      if (groep) {
        groep.cameras.push(camera);
      } else {
        perVeld.set(sleutel, { naam: veld, locatie, cameras: [camera] });
      }
    };

    for (const item of items) {
      const locatie = this.locatieVan(item);
      for (const { velden, camera } of this.camerasVan(item)) {
        for (const veld of velden) {
          zet(locatie, veld, camera);
        }
      }
    }

    return [...perVeld.values()]
      .map(({ naam, locatie, cameras }) => ({
        naam,
        locatie,
        zonderVeld: naam === ZONDER_VELD,
        cameras: cameras.sort((a, b) => a.naam.localeCompare(b.naam)),
      }))
      .sort((a, b) => {
        // De groep zonder veld hoort onderaan, wat zijn naam ook is.
        if (a.zonderVeld !== b.zonderVeld) {
          return a.zonderVeld ? 1 : -1;
        }
        return a.naam.localeCompare(b.naam);
      });
  }

  /** Waar de studio-pc staat; een producer kent (nog) geen locatie. */
  private locatieVan(item: ILive): string {
    return this.isRecorderType(item.type, item.originSource)
      ? (item.originSource.location ?? '').trim()
      : '';
  }

  /** De camera's van een studio, of de ene uitzending van een producer. */
  private camerasVan(item: ILive): Array<{ velden: string[]; camera: ICameraRegel }> {
    if (this.isRecorderType(item.type, item.originSource)) {
      return (item.originSource.streams ?? [])
        // Een camera die de studio zelf heeft uitgezet hoort hier niet te
        // staan; die kun je toch niet bekijken.
        .filter((stream) => stream.enabled !== false)
        .map((stream) => ({
          velden: this.veldenVan(stream),
          camera: this.cameraVanStream(stream, item),
        }));
    }

    // Een producer is één uitzending, geen lijst camera's. Hij hangt aan het
    // veld dat in de studio gekozen is; is dat er niet, dan onderaan.
    // De expliciete typecontrole is nodig: na de `return` hierboven weet
    // TypeScript nog niet dat er alleen een producer over is.
    const producer = this.isProducerType(item.type, item.originSource) ? item.originSource : null;
    const veld = producer?.pitches
      ?.find((pitch) => pitch.id === producer.selectedPitch)?.name;

    return [{
      velden: [veld?.trim() || ZONDER_VELD],
      camera: {
        sid: '',
        naam: item.name,
        status: item.status,
        teZien: item.hasPublicStream,
        bron: { type: item.type, sid: item.sid },
        instellingen: item.hasSettings ? item : undefined,
      },
    }];
  }

  private veldenVan(stream: IRecorderStream): string[] {
    const velden = (stream.pitches ?? [])
      .map((veld) => (veld ?? '').trim())
      .filter((veld) => veld.length > 0);

    return velden.length ? velden : [ZONDER_VELD];
  }

  private cameraVanStream(stream: IRecorderStream, item: ILive): ICameraRegel {
    // Staat de studio-pc uit (Arch, 31-10-2026), dan is er niets te zien en
    // neemt er niets op, wat de laatst bekende stand van de camera ook zegt:
    // die blijft staan zoals de pc hem achterliet (DESKTOP-THUTQI7 stond
    // sinds 26-10 uit en bleef in de lijst groen).
    const recorder = this.isRecorderType(item.type, item.originSource) ? item.originSource : null;
    if (studioOffline(recorder)) {
      return {
        sid: stream.sid,
        naam: stream.displayName || stream.cutout || item.name,
        status: LiveStatus.Offline,
        teZien: false,
        bron: { type: item.type, sid: item.sid },
        laatstGezien: recorder.lastSeen ?? undefined,
      };
    }

    const teZien = stream.protocol === RecorderStreamProtocol.WebRTC || !!stream.url;

    return {
      sid: stream.sid,
      // `displayName` is de naam die de club koos, `cutout` die van de
      // recorder zelf; zonder allebei valt de naam van de studio terug.
      naam: stream.displayName || stream.cutout || item.name,
      status: this.statusVanStream(stream, teZien),
      teZien,
      bron: { type: item.type, sid: item.sid },
    };
  }

  private statusVanStream(stream: IRecorderStream, teZien: boolean): LiveStatus {
    if (stream.status === RecorderStatus.Recording || !!stream.activeRecording) {
      return LiveStatus.Recording;
    }

    return teZien || stream.status === RecorderStatus.Streaming
      ? LiveStatus.Online
      : LiveStatus.Offline;
  }

  /**
   * De studio's (recorders) van de club, naast de producers. Stonden sinds
   * 13-07-2026 uitgecommentarieerd, waardoor een studio-camera die live naar
   * het platform stuurt nergens te vinden was (11-09-2026). Geen recht op de
   * studio-lijst, of de dienst ligt eruit: dan alleen de producers, zoals
   * het altijd was.
   */
  private getLiveFromRecorders$(): Observable<ILive[]> {
    return this.recorderService.list().pipe(
      map(result => (result?.recorders ?? []).map(recorder => this.mapRecorderToLive(recorder))),
      catchError(() => of([]))
    );
  }

  public isProducerType(type: LiveType, origin: IProducer | IRecorder): origin is IProducer {
    return type === LiveType.Producer;
  }

  public isRecorderType(type: LiveType, origin: IProducer | IRecorder): origin is IRecorder {
    return type === LiveType.Recorder;
  }

  private getLiveFromProducers$(): Observable<ILive[]> {
    return this.producerService.list().pipe(
      map(response => response.items.map(item => this.mapProducerToLive(item)))
    );
  }

  public mapProducerToLive(producer: IProducer): ILive {
    return {
      sid: producer.sid,
      name: producer.name,
      type: LiveType.Producer,
      status: LiveStatus[producer.status],
      hasPublicStream: this.getIsPublicExistsInStream(producer.streams ?? []),
      hasSettings: producer.pitches.length && producer.type !== ProducerType.Improvis,
      originSource: producer
    }
  }

  public mapRecorderToLive(recorder: IRecorder): ILive {
    // Een camera die live naar het platform staat (WebRTC) is te bekijken;
    // `url` is voor een clubgebruiker altijd geblankt, dus daar kun je niet
    // op af. Zo'n studio is online, wat de hartslag ook zegt.
    const streams = recorder.streams ?? [];
    // Bewust NIET meer afhankelijk van het recht "live meekijken": de server
    // bewaakt dat bij het opvragen van de kijklink (403), en de studiopagina
    // legt dan uit welk recht ontbreekt. Verborg het scherm de knop op basis
    // van een verouderde rechtenlijst, dan zag een club alleen een oranje
    // stip zonder enige uitleg (13-09-2026).
    const liveCamera = streams.some((stream) => stream.protocol === RecorderStreamProtocol.WebRTC);
    // Behalve als Arch zeker weet dat de pc uit staat (31-10-2026); weet
    // Arch het niet, dan blijft het zoals het was.
    const offline = studioOffline(recorder);
    return {
      sid: recorder.sid,
      name: recorder.name,
      type: LiveType.Recorder,
      status: !offline && (liveCamera || recorder.isActive) ? LiveStatus.Online : LiveStatus.Offline,
      hasPublicStream: !offline && (liveCamera || streams.some((stream) => !!stream.url)),
      hasSettings: false,
      originSource: recorder
    }
  }

  private getIsPublicExistsInStream(streams: IStream[]): boolean {
    for (const stream of streams) {
      if (stream.streamType === StreamTypeEnum.Public) {
        return true;
      }
    }

    return false;
  }
}
